import { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Footer from '../home/components/Footer';
import { useLanguage } from '../../contexts/LanguageContext';

export default function ContactPage() {
  const { language } = useLanguage();

  useEffect(() => {
    // 页面加载时滚动到顶部
    window.scrollTo(0, 0);
  }, []);

  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const content = {
    zh: {
      contactUs: '联系我们',
      heroSubtitle: '无论您有任何疑问、合作意向或创意想法，我们都期待听到您的声音',
      getInTouch: '与我们取得联系',
      teamReady: '我们的团队随时准备为您提供支持，期待您的来信',
      hongKongOffice: '香港办公室',
      hongKongAddress: '香港数码港道100号<br />数码港2座智能空间2',
      shenzhenOffice: '深圳办公室',
      shenzhenAddress: '深圳市南山区南山智园<br />C2栋16楼',
      email: '邮箱',
      phone: '电话',
      businessHours: '营业时间',
      mondayFriday: '周一至周五',
      saturday: '周六',
      sundayHolidays: '周日及公众假期',
      closed: '休息',
      followUs: '关注我们',
      sendMessage: '发送消息',
      formDescription: '填写下方表单，我们会尽快回复您',
      yourName: '您的姓名',
      emailLabel: '邮箱',
      phoneNumber: '电话号码',
      companyName: '公司名称',
      message: '留言',
      namePlaceholder: '请输入您的姓名',
      emailPlaceholder: 'your@email.com',
      phonePlaceholder: '+852 或 +86',
      companyPlaceholder: '您的公司名称',
      messagePlaceholder: '请告诉我们您的需求或问题...',
      sending: '发送中...',
      sendMessageBtn: '发送消息',
      successMessage: '消息发送成功！我们会尽快与您联系。',
      ourLocations: '我们的位置',
      locationsDescription: '欢迎到访我们的办公室进行交流',
      mondayFridayTime: '09:00 - 18:00',
      saturdayTime: '10:00 - 16:00'
    },
    en: {
      contactUs: 'Contact Us',
      heroSubtitle: 'Whether you have any questions, partnership intentions, or creative ideas, we look forward to hearing from you',
      getInTouch: 'Get In Touch With Us',
      teamReady: 'Our team is ready to support you anytime, looking forward to hearing from you',
      hongKongOffice: 'Hong Kong Office',
      hongKongAddress: 'Smart Space 2, Cyberport 2<br />100 Cyberport Road, Hong Kong',
      shenzhenOffice: 'Shenzhen Office',
      shenzhenAddress: 'Floor 16, Building C2<br />Nanshan Intelligent Park, Nanshan District, Shenzhen',
      email: 'Email',
      phone: 'Phone',
      businessHours: 'Business Hours',
      mondayFriday: 'Monday - Friday',
      saturday: 'Saturday',
      sundayHolidays: 'Sunday & Public Holidays',
      closed: 'Closed',
      followUs: 'Follow Us',
      sendMessage: 'Send Message',
      formDescription: 'Fill out the form below and we will get back to you as soon as possible',
      yourName: 'Your Name',
      emailLabel: 'Email',
      phoneNumber: 'Phone Number',
      companyName: 'Company Name',
      message: 'Message',
      namePlaceholder: 'Please enter your name',
      emailPlaceholder: 'your@email.com',
      phonePlaceholder: '+852 or +86',
      companyPlaceholder: 'Your company name',
      messagePlaceholder: 'Please tell us your needs or questions...',
      sending: 'Sending...',
      sendMessageBtn: 'Send Message',
      successMessage: 'Message sent successfully! We will contact you as soon as possible.',
      ourLocations: 'Our Locations',
      locationsDescription: 'Welcome to visit our offices for communication',
      mondayFridayTime: '09:00 - 18:00',
      saturdayTime: '10:00 - 16:00'
    }
  };

  const t = content[language];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 准备表单数据
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('recipient', 'comiai_talent@outlook.com');

      // 提交到表单服务
      const response = await fetch('https://readdy.ai/api/form/d5rlbqihs78ip5370jag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        // 提交成功
        alert(language === 'zh' ? '感谢您的留言！我们会尽快回复您。' : 'Thank you for your message! We will get back to you soon.');
        // 清空表单
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      } else {
        throw new Error('提交失败');
      }
    } catch (error) {
      console.error('表单提交错误:', error);
      alert(language === 'zh' ? '提交失败，请稍后重试。' : 'Submission failed, please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white">
      <Navbar scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-red-900 to-gray-900"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-transparent"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="inline-block mb-6 px-6 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full">
            <p className="text-xs font-light text-white tracking-widest uppercase">
              {t.contactUs}
            </p>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {t.contactUs}
          </h1>
          
          <p className="text-lg text-white/90 max-w-2xl mx-auto leading-relaxed">
            {t.heroSubtitle}
          </p>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left: Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {t.getInTouch}
                </h2>
                <p className="text-base text-gray-600 leading-relaxed">
                  {t.teamReady}
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-5">
                {/* 香港办公室 */}
                <div className="group bg-white rounded-3xl p-8 hover:shadow-2xl transition-all border border-gray-200 hover:border-red-200">
                  <div className="flex items-start space-x-5">
                    <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                      <i className="ri-map-pin-line text-3xl text-white"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{t.hongKongOffice}</h3>
                      <p className="text-base text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: t.hongKongAddress }}></p>
                    </div>
                  </div>
                </div>

                {/* 深圳办公室 */}
                <div className="group bg-white rounded-3xl p-8 hover:shadow-2xl transition-all border border-gray-200 hover:border-red-200">
                  <div className="flex items-start space-x-5">
                    <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                      <i className="ri-building-2-line text-3xl text-white"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{t.shenzhenOffice}</h3>
                      <p className="text-base text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: t.shenzhenAddress }}></p>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="group bg-white rounded-3xl p-8 hover:shadow-2xl transition-all border border-gray-200 hover:border-red-200">
                  <div className="flex items-start space-x-5">
                    <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                      <i className="ri-mail-line text-3xl text-white"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{t.email}</h3>
                      <a 
                        href="mailto:comiai_talent@outlook.com" 
                        className="text-base text-red-600 hover:text-red-700 transition-colors cursor-pointer font-medium"
                      >
                        comiai_talent@outlook.com
                      </a>
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div className="group bg-white rounded-3xl p-8 hover:shadow-2xl transition-all border border-gray-200 hover:border-red-200">
                  <div className="flex items-start space-x-5">
                    <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                      <i className="ri-phone-line text-3xl text-white"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{t.phone}</h3>
                      <a 
                        href="tel:+85257469338" 
                        className="text-base text-green-600 hover:text-green-700 transition-colors cursor-pointer font-medium"
                      >
                        +852 5746 9338
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Working Hours */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-3xl p-8 border border-gray-200">
                <div className="flex items-start space-x-5">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex-shrink-0 shadow-lg">
                    <i className="ri-time-line text-3xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{t.businessHours}</h3>
                    <div className="space-y-2 text-base text-gray-700">
                      <div className="flex justify-between">
                        <span className="font-medium">{t.mondayFriday}</span>
                        <span>{t.mondayFridayTime}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">{t.saturday}</span>
                        <span>{t.saturdayTime}</span>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span className="font-medium">{t.sundayHolidays}</span>
                        <span>{t.closed}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-3xl p-8 border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">{t.followUs}</h3>
                <div className="flex items-center space-x-4">
                  <a 
                    href="#" 
                    className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl hover:from-red-600 hover:to-red-700 hover:shadow-lg transition-all cursor-pointer group"
                  >
                    <i className="ri-twitter-x-line text-2xl text-gray-700 group-hover:text-white transition-colors"></i>
                  </a>
                  <a 
                    href="#" 
                    className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl hover:from-red-600 hover:to-red-700 hover:shadow-lg transition-all cursor-pointer group"
                  >
                    <i className="ri-linkedin-fill text-2xl text-gray-700 group-hover:text-white transition-colors"></i>
                  </a>
                  <a 
                    href="#" 
                    className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl hover:from-red-600 hover:to-red-700 hover:shadow-lg transition-all cursor-pointer group"
                  >
                    <i className="ri-wechat-line text-2xl text-gray-700 group-hover:text-white transition-colors"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div className="lg:sticky lg:top-24 h-fit">
              <div className="bg-white rounded-3xl p-10 shadow-xl border border-gray-200">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    {t.sendMessage}
                  </h2>
                  <p className="text-base text-gray-600">
                    {t.formDescription}
                  </p>
                </div>

                <form 
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit} 
                  className="space-y-6"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2">
                      {t.yourName} <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 text-sm bg-gray-50 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all"
                      placeholder={t.namePlaceholder}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                      {t.emailLabel} <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 text-sm bg-gray-50 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all"
                      placeholder={t.emailPlaceholder}
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-2">
                      {t.phoneNumber}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-5 py-4 text-sm bg-gray-50 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all"
                      placeholder={t.phonePlaceholder}
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-900 mb-2">
                      {t.companyName}
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-5 py-4 text-sm bg-gray-50 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all"
                      placeholder={t.companyPlaceholder}
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2">
                      {t.message} <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-5 py-4 text-sm bg-gray-50 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all resize-none"
                      placeholder={t.messagePlaceholder}
                    ></textarea>
                    <p className="mt-2 text-xs text-gray-500 text-right">
                      {formData.message.length}/500
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-5 bg-gradient-to-r from-red-600 to-red-700 text-white text-base font-semibold rounded-2xl hover:from-red-700 hover:to-red-800 hover:shadow-xl transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center space-x-2">
                        <i className="ri-loader-4-line animate-spin text-xl"></i>
                        <span>{t.sending}</span>
                      </span>
                    ) : (
                      <span className="flex items-center justify-center space-x-2">
                        <span>{t.sendMessageBtn}</span>
                        <i className="ri-send-plane-fill text-xl"></i>
                      </span>
                    )}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="p-5 bg-green-50 border border-green-200 rounded-2xl">
                      <div className="flex items-center space-x-3">
                        <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                        <p className="text-sm text-green-800 font-medium">
                          {t.successMessage}
                        </p>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t.ourLocations}
            </h2>
            <p className="text-base text-gray-600">
              {t.locationsDescription}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* 香港地图 */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-200">
              <div className="p-6 bg-gradient-to-r from-red-600 to-red-700">
                <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
                  <i className="ri-map-pin-line text-2xl"></i>
                  <span>{t.hongKongOffice}</span>
                </h3>
              </div>
              <div className="aspect-video">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3690.7486!2d114.128!3d22.2627!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDE1JzQ1LjciTiAxMTTCsDA3JzQwLjgiRQ!5e0!3m2!1sen!2shk!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

            {/* 深圳地图 */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-200">
              <div className="p-6 bg-gradient-to-r from-gray-800 to-gray-900">
                <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
                  <i className="ri-building-2-line text-2xl"></i>
                  <span>{t.shenzhenOffice}</span>
                </h3>
              </div>
              <div className="aspect-video">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3686.8!2d113.9413!3d22.5423!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDMyJzMyLjMiTiAxMTPCsDU2JzI4LjciRQ!5e0!3m2!1sen!2scn!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
